import { ApiResponse, BaseApiResponse, HttpResponse } from './Api';

export const isBaseApiResponse = (value: unknown): value is BaseApiResponse =>
    typeof value === 'object' &&
    value !== null &&
    typeof (value as BaseApiResponse).statusCode === 'number' &&
    typeof (value as BaseApiResponse).success === 'boolean';

/**
 * Every endpoint answers with the ApiResponse envelope. This turns a call into its payload, and any
 * failure -- a non-2xx answer, a success:false envelope, a network error -- into a BaseApiResponse,
 * so queries, mutations and the sagas' callers all see one error shape.
 */
export async function unwrapApiResponse<TData>(call: Promise<HttpResponse<ApiResponse<TData>, BaseApiResponse>>): Promise<TData> {
    let response: HttpResponse<ApiResponse<TData>, BaseApiResponse>;
    try {
        response = await call;
    } catch (error) {
        throw toBaseApiResponse(error);
    }

    if (response.data?.success !== true) {
        throw toBaseApiResponse(response);
    }
    return response.data.data as TData;
}

/**
 * The envelope a failure carries, wherever it sits: the error itself, the body of a 2xx answer that
 * said success:false, or the body of a non-2xx answer. Anything else -- an unparsable body, a
 * network error -- becomes an envelope built from the HTTP status.
 */
export function toBaseApiResponse(error: unknown): BaseApiResponse {
    if (isBaseApiResponse(error)) return error;

    const failure = error as { data?: unknown; error?: unknown; status?: number; statusText?: string } | null | undefined;
    if (isBaseApiResponse(failure?.data)) return failure.data;
    if (isBaseApiResponse(failure?.error)) return failure.error;

    return {
        success: false,
        statusCode: typeof failure?.status === 'number' && failure.status >= 400 ? failure.status : 500,
        message: failure?.statusText || 'An unknown error occurred'
    };
}
